import { addParamsToString } from "./url";
import { setTimeout } from "./interval";

let pageHead: HTMLElement;
function getPageHead() {
  return (pageHead =
    pageHead ||
    document.getElementsByTagName("head")[0] ||
    document.documentElement ||
    document.body);
}

/**
 * 通过自定义方式加载资源
 * @param tagName 加载资源用的dom节点名称，可选 script / link / img
 * @param url 加载的资源路径
 * @param attrsMap 附加dom上的其他属性
 */
function loadResource(
  tagName: "link" | "script" | "img",
  url: string,
  attrsMap: Record<string, string> = {}
): Promise<null> {
  return new Promise(function (resolve, reject) {
    const head = getPageHead();
    const tag = <HTMLScriptElement | HTMLLinkElement>(
      document.createElement(tagName)
    );
    for (const attr in attrsMap) {
      tag.setAttribute(attr, attrsMap[attr]);
    }
    tag.onload = function () {
      head.removeChild(tag);
      resolve(null);
    };
    tag.onerror = reject;
    tag.setAttribute(tagName === "link" ? "href" : "src", url);
    head.appendChild(tag);
  });
}

/**
 * 加载一个js文件
 * @param js 需要加载的js资源路径
 */
export const loadJS = (js: string) =>
  loadResource("script", js, { type: "text/javascript", charset: "utf-8" });

/**
 * 加载一个css文件
 * @param {String} css 需要加载的css资源路径
 */
export const loadCss = (css: string) =>
  loadResource("link", css, {
    type: "text/css",
    rel: "stylesheet",
    charset: "utf-8",
  });

export interface ImageDescriptor {
  width?: number;
  height?: number;
  image: HTMLImageElement;
}
const cacheHolder: Record<string, HTMLImageElement> = {};

/**
 * 加载一个远程图片
 * @param imgUrl 远程图片的url地址
 * @param timeout 超时时间，单位 ms，默认 3000
 * @param holderCache 是否保存在全局缓存中防止被 GC 回收，默认 false
 */
export const loadImage = (
  imgUrl: string,
  timeout: number = 3000,
  holderCache: boolean = false
): Promise<ImageDescriptor> =>
  new Promise((resolve, reject) => {
    const img = new Image();
    const randomProp = "image" + Math.random().toString(16).slice(2);
    if (holderCache) {
      cacheHolder[randomProp] = img;
    }
    const finishHandler = function () {
      resolve({
        width: img.width,
        height: img.height,
        image: img,
      });
    };
    // 监听加载
    img.onload = finishHandler;
    img.onerror = function () {
      delete cacheHolder[randomProp];
      reject(null);
    };
    setTimeout(timeout, function () {
      finishHandler();
    });
    img.src = imgUrl;
  });

/**
 * 发送JSONP请求
 * @param url 要请求的 jsonp 地址，回调函数名以 {callback} 作为占位，默认追加 callback 参数
 * @param params 要追加的query数据
 */
export function jsonp<T>(
  url: string,
  params?: string | Record<string, string | number | boolean>
): Promise<null | T> {
  const callbackFnName = "jsonpCallback" + Math.random().toString(16).slice(2);
  if (!url) {
    return Promise.reject(null);
  }
  url = "" + url;
  return new Promise((resolve, reject) => {
    let fullUrl = addParamsToString(url, params || {}, true);
    if (url.indexOf("{callback}") === -1) {
      fullUrl = addParamsToString(fullUrl, "callback=" + callbackFnName);
    } else {
      fullUrl = fullUrl.replace(/\{callback\}/gi, callbackFnName);
    }
    // @ts-ignore
    window[callbackFnName] = function (data: T) {
      resolve(data);
      // @ts-ignore
      delete window[callbackFnName];
    };
    loadJS(fullUrl).catch(function () {
      reject(null);
      // @ts-ignore
      delete window[callbackFnName];
    });
  });
}

/**
 * 批量异步加载远程图片
 * @param imageList 图片列表
 * @param timeout 超时时间，默认 3000，单位 ms
 */
export function preloadImages(
  imageList: string | string[],
  timeout: number = 3000
) {
  const arr = Array.isArray(imageList) ? imageList : [imageList];
  return Promise.all(
    arr.map((url) =>
      loadImage(url, timeout, true).catch((e) => {
        // 图片加载错误，仅仅打印日志，不终止任何流程
        console.error("load image error", url, e);
        return null;
      })
    )
  );
}
